import * as React from 'react';

import TextareaAutosize, { type TextareaAutosizeProps } from 'react-textarea-autosize';

import { cn } from '@/lib/utils';

import { focusVisibleRingError, focusVisibleRing } from './constants';

const baseClass =
  'flex w-full rounded-md border border-border bg-background px-3 py-2 text-base transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

type TextareaProps = React.ComponentProps<'textarea'> & {
  ref?: React.Ref<HTMLTextAreaElement>;
  /** Auto-grow with content. Pass true for default behavior or an object to clamp rows. */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  minRows?: number;
  maxRows?: number;
};

const Textarea = (allProps: TextareaProps) => {
  const { className, ref, autoSize, minRows, maxRows, ...props } = allProps;
  // Keyed on whether a sizing prop was passed at all, not on its current value. A consumer
  // toggling `maxRows` to undefined is lifting the row cap, not opting out of auto-sizing,
  // and must not be swapped for the fixed `min-h-16` textarea mid-interaction.
  // `autoSize={false}` stays an explicit opt-out.
  const wantsAutoSize =
    autoSize !== false &&
    ('autoSize' in allProps || 'minRows' in allProps || 'maxRows' in allProps);
  const resolvedMinRows = minRows ?? (typeof autoSize === 'object' ? autoSize.minRows : undefined);
  const resolvedMaxRows = maxRows ?? (typeof autoSize === 'object' ? autoSize.maxRows : undefined);

  if (wantsAutoSize) {
    return (
      <TextareaAutosize
        ref={ref}
        minRows={resolvedMinRows}
        maxRows={resolvedMaxRows}
        className={cn(baseClass, className, focusVisibleRing, focusVisibleRingError)}
        {...(props as TextareaAutosizeProps)}
      />
    );
  }

  return (
    <textarea
      ref={ref}
      className={cn(
        baseClass,
        'field-sizing-content min-h-16',
        className,
        focusVisibleRing,
        focusVisibleRingError
      )}
      {...props}
    />
  );
};

export { Textarea };
